import React, { useMemo } from 'react';
import { BsCaretDownFill, BsCaretRightFill, BsCurrencyDollar, BsCheckCircle, BsFileEarmarkText, BsHourglassSplit, BsClock } from "react-icons/bs";
import { BillingUtils } from '../Services/billingService';

const BillingKPI = ({ activities = [], filteredActivities = [], selectedActivities = [] }) => {
  const [showKPI, setShowKPI] = React.useState(true);

  // Ensure filteredActivities is used if it exists, otherwise fall back to all activities
  const effectiveActivities = useMemo(() => {
    // If filteredActivities is provided and different from all activities, use it
    if (filteredActivities && filteredActivities.length > 0 && 
        filteredActivities.length !== activities.length) {
      return filteredActivities;
    }
    // Otherwise use the full dataset
    return activities;
  }, [activities, filteredActivities]);

  // Calculate all KPIs based on current effective activities
  const kpis = useMemo(() => {
    console.log('🔍 [BillingKPI] Calculating KPIs for activities:', effectiveActivities);
    
    // No data case
    if (!effectiveActivities || effectiveActivities.length === 0) {
      return {
        totalActivities: 0,
        totalAmount: 0,
        exportedActivities: 0,
        exportedAmount: 0,
        partialActivities: 0,
        partialAmount: 0,
        pendingActivities: 0,
        pendingAmount: 0,
        selectedCount: 0,
        selectedAmount: 0
      };
    }

    let totalAmount = 0;
    let exportedActivities = 0;
    let exportedAmount = 0;
    let partialActivities = 0;
    let partialAmount = 0;
    let pendingActivities = 0;
    let pendingAmount = 0;
    let selectedAmount = 0;

    // Get selected activity IDs for calculations
    const selectedIds = new Set(selectedActivities);

    effectiveActivities.forEach(activity => {
      console.log('🔍 [BillingKPI] Processing activity:', activity);
      
      // Get net amount - check both structures
      let netAmount = 0;
      if (activity.net_amount !== undefined) {
        // Transformed data structure (from BillingTable)
        netAmount = activity.net_amount || 0;
      } else if (activity.amounts?.net !== undefined) {
        // Original API data structure
        netAmount = activity.amounts.net || 0;
      }
      
      totalAmount += netAmount;
      console.log('💰 Activity net amount:', netAmount, 'Total so far:', totalAmount);

      // Calculate selected amount
      const activityId = activity.id || activity._id;
      if (selectedIds.has(activityId)) {
        selectedAmount += netAmount;
      }

      // ✅ NUEVA LÓGICA: Determinar estado de exportación Odoo
      const soLinks = activity._original?.so_links || activity.so_links || [];
      
      if (soLinks.length === 0) {
        // Sin sales orders = Pendiente
        pendingActivities++;
        pendingAmount += netAmount;
      } else {
        // Contar cuántas están exportadas
        const exportedCount = soLinks.filter(so => 
          so.odoo?.order_id || 
          so.odoo_order_id || 
          so.so_status_snapshot === 'exported'
        ).length;
        
        const totalCount = soLinks.length;
        
        if (exportedCount === 0) {
          // Tiene sales orders pero ninguna exportada = Pendiente
          pendingActivities++;
          pendingAmount += netAmount;
        } else if (exportedCount === totalCount) {
          // Todas exportadas = Exportado
          exportedActivities++;
          exportedAmount += netAmount;
        } else {
          // Algunas exportadas = Parcial
          partialActivities++;
          partialAmount += netAmount;
        }
      }
      
      console.log('📊 Activity Odoo status - SO Links:', soLinks.length, 'Exported:', exportedActivities, 'Partial:', partialActivities, 'Pending:', pendingActivities);
    });

    const result = {
      totalActivities: effectiveActivities.length,
      totalAmount,
      exportedActivities,
      exportedAmount,
      partialActivities,
      partialAmount,
      pendingActivities,
      pendingAmount,
      selectedCount: selectedActivities.length,
      selectedAmount
    };
    
    console.log('📊 [BillingKPI] Final KPIs:', result);
    return result;
  }, [effectiveActivities, selectedActivities]);

  // Format number with commas for thousands
  const formatNumber = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  // Format currency in EUR
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  // Toggle KPI visibility
  const toggleKPI = () => {
    setShowKPI(!showKPI);
  };

  // Determine if filters are active
  const isFiltered = filteredActivities && 
                    activities && 
                    filteredActivities.length !== activities.length &&
                    filteredActivities.length > 0;

  // Define the KPI cards data
  const kpiCards = [
    { 
      title: 'Total', 
      value: kpis.totalActivities,
      subtitle: formatCurrency(kpis.totalAmount),
      icon: <BsFileEarmarkText />, 
      iconColor: '#f5f2e8'
    },
    { 
      title: 'Exportadas', 
      value: kpis.exportedActivities,
      subtitle: formatCurrency(kpis.exportedAmount),
      icon: <BsCheckCircle />, 
      iconColor: '#e8f4fd'
    },
    { 
      title: 'Parciales', 
      value: kpis.partialActivities,
      subtitle: formatCurrency(kpis.partialAmount),
      icon: <BsHourglassSplit />, 
      iconColor: '#fde8f4'
    },
    { 
      title: 'Pendientes', 
      value: kpis.pendingActivities,
      subtitle: formatCurrency(kpis.pendingAmount),
      icon: <BsClock />, 
      iconColor: '#f0e8fd'
    },
    { 
      title: 'Seleccionadas', 
      value: kpis.selectedCount,
      subtitle: formatCurrency(kpis.selectedAmount),
      icon: <BsFileEarmarkText />, 
      iconColor: '#e8f5e8'
    }
  ];

  return (
    <>
            
      {showKPI && (
        <div className="row gy-3 introbx_row">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="col">
              <div className="d-flex align-items-center gap-2 p-3 rounded-10 shadow-sm">
                <div className="icon flex-shrink-0 d-flex align-items-center justify-content-center rounded-circle" style={{ width: '2rem', height: '2rem', backgroundColor: '#E4E4FF', color: '#8381FF' }}>
                  <span className="d-block material-symbols-outlined fs-lg">{kpi.icon}</span>
                </div>
                <div className="info flex-fill">
                  <p className="fs-sm text-gray mb-0">{kpi.title}</p>
                  <p className="lh-1 fw-semibold">{formatNumber(kpi.value)}</p>
                  {kpi.subtitle && (
                    <p className="fs-xs text-muted mb-0">{kpi.subtitle}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default BillingKPI;